import React, { useEffect, useRef } from "react";

import { Layout } from "@/layout/layout";

declare global {
  interface Window {
    renderMathInElement?: (
      element: HTMLElement,
      options?: {
        delimiters?: { left: string; right: string; display: boolean }[];
        throwOnError?: boolean;
      },
    ) => void;
  }
}

export const HowItWorks = () => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    let attempts = 0;
    const maxAttempts = 20;

    const renderMath = () => {
      if (!contentRef.current || !window.renderMathInElement) return false;

      window.renderMathInElement(contentRef.current, {
        delimiters: [
          { left: "$$", right: "$$", display: true },
          { left: "\\(", right: "\\)", display: false },
          { left: "\\[", right: "\\]", display: true },
        ],
        throwOnError: false,
      });

      return true;
    };

    if (renderMath()) return;

    const interval = window.setInterval(() => {
      attempts += 1;
      if (renderMath() || attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 150);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  return (
    <Layout>
      <div className="min-h-screen w-full flex flex-col gap-8 py-10 px-3 md:px-16 dark:bg-dark-bg">
        <div
          ref={contentRef}
          className="w-full max-w-5xl mx-auto flex flex-col gap-6"
        >
          <div className="rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800 p-7 md:p-10">
            <h1 className="font-sf-pro text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white">
              How Funds Work
            </h1>
            <p className="mt-3 text-m md:text-l text-gray-600 dark:text-gray-300 leading-relaxed">
              A fund is a weighted basket of Cardano native tokens. Deposits and
              withdrawals are executed with integer math on-chain, while wallets
              show decimal-friendly values for humans.
            </p>
          </div>

          <div className="rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800 p-6">
            <h2 className="font-sf-pro text-xl font-semibold text-gray-900 dark:text-white">
              Why We Use Integers
            </h2>
            <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Smart contracts must use exact arithmetic. To avoid rounding errors,
              Cardano stores asset amounts as integers (base units) on-chain.
              Decimals are only a display rule used by wallets and UIs.
            </p>
            <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Decimals are a display convention only; the contract still stores
              and checks integer base units.
            </p>
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              {"$$\\text{display amount}=\\frac{\\text{base units}}{10^{\\text{decimals}}}$$"}
            </div>
            <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              The fund protocol keeps everything integer-safe by using token
              factors, a fund factor, and an integer deposit multiple.
            </p>
          </div>

          <div className="rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800 p-6">
            <h2 className="font-sf-pro text-xl font-semibold text-gray-900 dark:text-white">
              Explicit Example
            </h2>
            <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Someone creates a fund with two tokens, A and B. At creation time,
              Token A price is 2 ADA, Token B price is 1 ADA, and value
              allocation is 50/50.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm md:text-base text-gray-700 dark:text-gray-200">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3 font-semibold">Token</th>
                    <th className="py-2 pr-3 font-semibold">Price at Creation</th>
                    <th className="py-2 pr-3 font-semibold">Decimals</th>
                    <th className="py-2 font-semibold">Value Allocation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-3">Token A</td>
                    <td className="py-2 pr-3">2 ADA</td>
                    <td className="py-2 pr-3">6</td>
                    <td className="py-2">50%</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-3">Token B</td>
                    <td className="py-2 pr-3">1 ADA</td>
                    <td className="py-2 pr-3">6</td>
                    <td className="py-2">50%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Because prices differ, equal value allocation gives a different
              token count allocation. Quantity ratio is derived from
              allocation/price:
            </p>
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              {"$$q_A:q_B=\\frac{0.5}{2}:\\frac{0.5}{1}=1:2$$"}
              {"$$\\text{token factors: }f_A=1,\\;f_B=2$$"}
            </div>
            <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Assume creator fee is 5%. We need exact integer fee
              representation. Since 5% = 1/20, fund factor must be a multiple of
              20 to keep fee exact. We pick 100 (a convenient power of ten), so
              royalty factor is 5:
            </p>
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              {"$$\\text{fee ratio}=\\frac{5}{100}=0.05=5\\%$$"}
              {"$$\\text{fund factor}=100,\\;\\text{royalty factor}=5$$"}
            </div>
            <p className="mt-4 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              How fund factor is computed in general:
            </p>
            <ol className="mt-2 list-decimal list-inside text-sm md:text-base text-gray-600 dark:text-gray-300 space-y-1 leading-relaxed">
              <li>Convert fee percent into an exact fraction: fee_ratio = fee_num / fee_den.</li>
              <li>Compute preliminary token values from price, weight, and min_deposit_used.</li>
              <li>Try candidate fund factors {"{1, 10, 100, 1000, 10000, 100000}"} and keep only values greater than fee_den.</li>
              <li>Pick the largest candidate that keeps every token above 0 after scaling (so each token factor stays usable).</li>
              <li>Set token_factor_i = floor(raw_i / fund_factor), then compute royalty_factor = fee_num * fund_factor / fee_den.</li>
            </ol>
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              {"$$\\text{fee ratio}=\\frac{\\text{royalty factor}}{\\text{fund factor}}\\text{ (exact)}$$"}
            </div>
            <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Now when depositing the user chooses a deposit multiple of 2 (the
              user can choose any integer multiple based on deposit size; it
              must be integer so required token amounts, fee shares, and minted
              shares remain exact and ratio-preserving).
            </p>
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              {"$$\\text{required Token A}=1\\times100\\times2=200\\text{ base units}$$"}
              {"$$\\text{required Token B}=2\\times100\\times2=400\\text{ base units}$$"}
              {"$$\\text{Token A display}=\\frac{200}{10^6}=0.000200$$"}
              {"$$\\text{Token B display}=\\frac{400}{10^6}=0.000400$$"}
              {"$$\\text{minted shares}=100\\times2=200$$"}
              {"$$\\text{fee shares}=5\\times2=10$$"}
              {"$$\\text{user receives}=200-10=190$$"}
              {"$$\\text{fee percent}=\\frac{5}{100}\\times 100=5\\%$$"}
            </div>
          </div>

          <div className="rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800 p-6">
            <h2 className="font-sf-pro text-xl font-semibold text-gray-900 dark:text-white">
              Math Overview (General Formulas)
            </h2>
            <div className="mt-4 text-sm text-gray-700 dark:text-gray-200">
              {"$$\\text{required token}_i=\\text{token factor}_i\\times\\text{fund factor}\\times\\text{multiple}$$"}
              {"$$\\text{minted shares}=\\text{fund factor}\\times\\text{multiple}$$"}
              {"$$\\text{fee shares}=\\text{royalty factor}\\times\\text{multiple}$$"}
              {"$$\\text{net shares}=\\text{minted shares}-\\text{fee shares}$$"}
              {"$$\\text{fee ratio}=\\frac{\\text{royalty factor}}{\\text{fund factor}}$$"}
              {"$$\\text{display amount}_i=\\frac{\\text{base units}_i}{10^{\\text{decimals}_i}}$$"}
            </div>
            <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Token allocation is value-based at creation, then enforced as fixed
              integer token ratios on-chain.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};
